import api, { prefs as prefsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: prefs } = useQuery({ queryKey: ["prefs"], queryFn: prefsApi.get });

  // Rank title, not the raw pilots.rank value. `user.rank` is the §17 index
  // ("6"), which is meaningless to a pilot — the dashboard already resolves it
  // through the profile card, and this page was showing the number.
  //
  // Same queryKey as PilotHeader, so react-query serves it from cache rather
  // than issuing a second request.
  const { data: card } = useQuery({
    queryKey: ["profile-card", user?.pid],
    queryFn: () => api.get("/pilot/profile-card").then((r) => r.data),
    enabled: !!user?.pid,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const [sbUserid, setSbUserid] = useState("");
  const [autoFile, setAutoFile] = useState(true);
  const isDesktop = typeof window !== "undefined" && !!window.electronAPI;
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  useEffect(() => {
    if (prefs) {
      setSbUserid(prefs.simbrief_userid || "");
      setAutoFile(prefs.auto_file_pirep ?? true);
    }
  }, [prefs]);
  useEffect(() => {
    if (!isDesktop) return;
    window.electronAPI.prefs.get().then((p) => {
      if (p && Object.prototype.hasOwnProperty.call(p, "overlayEnabled")) {
        setOverlayEnabled(!!p.overlayEnabled);
      }
    });
    // Ctrl+Alt+H and the tray can change it while this page is open.
    // Optional chaining: a desktop shell older than this page has no onChanged.
    return window.electronAPI.overlay?.onChanged?.((st) => setOverlayEnabled(!!st.enabled));
  }, [isDesktop]);

  const toggleOverlay = (next) => {
    setOverlayEnabled(next);
    if (isDesktop) {
      // Both channels stay in sync: prefs:set persists the value, overlay:setEnabled
      // also nudges the main process to show/hide immediately.
      window.electronAPI.prefs.set({ overlayEnabled: next });
      window.electronAPI.overlay.setEnabled(next);
    }
  };

  const savePrefs = useMutation({
    mutationFn: () => prefsApi.update({
      simbrief_userid: sbUserid || null,
      auto_file_pirep: autoFile,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["prefs"] }); toast.success("Settings saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  return (
    <div className="p-8 max-w-3xl" data-testid="settings-page">
      <div className="mb-6">
        <div className="overline mb-1">Configuration</div>
        <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pilot preferences and account info.
        </p>
      </div>

      <section className="card-flat p-6 mb-6" data-testid="settings-simbrief">
        <div className="overline mb-1">Dispatch</div>
        <h2 className="text-base font-bold mb-3">SimBrief profile</h2>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          VACARS5 pulls your generated OFP directly from SimBrief using your
          Pilot ID. Find it under <span className="font-mono text-primary">simbrief.com → Account Settings</span>.
        </p>
        <label className="block mb-4 max-w-xs">
          <span className="overline block mb-1.5">SimBrief Pilot ID (numeric)</span>
          <input
            type="text"
            value={sbUserid}
            onChange={(e) => setSbUserid(e.target.value.replace(/\D/g, ""))}
            placeholder="e.g. 123456"
            data-testid="settings-simbrief-userid"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
          />
        </label>
      </section>

      <section className="card-flat p-6 mb-6" data-testid="settings-autofile">
        <div className="overline mb-1">ACARS</div>
        <h2 className="text-base font-bold mb-3">Auto-file PIREP on arrival</h2>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Files the flight automatically once you're at the gate with the parking
          brake set and the engines shut down, as in VACARS3. Turn off if you
          want to file manually from Live Tracking.
        </p>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-autofile-toggle">
          <input
            type="checkbox"
            checked={autoFile}
            onChange={(e) => setAutoFile(e.target.checked)}
            className="w-4 h-4 accent-primary"
          />
          <span className="text-sm">{autoFile ? "Enabled" : "Disabled"}</span>
        </label>
      </section>

      {isDesktop && (
        <section className="card-flat p-6 mb-6" data-testid="settings-overlay">
          <div className="overline mb-1">Desktop</div>
          <h2 className="text-base font-bold mb-3">In-sim HUD overlay</h2>
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            Shows a slim always-on-top strip at the top of your screen while a
            flight is INITed — flight number, ORIG → DEST, distance remaining,
            altitude, speed, and your landing rating on touchdown. Click-through,
            so it never steals focus from MSFS / Prepar3D / X-Plane.
          </p>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-overlay-toggle">
            <input
              type="checkbox"
              checked={overlayEnabled}
              onChange={(e) => toggleOverlay(e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm">{overlayEnabled ? "Enabled" : "Disabled"}</span>
          </label>

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={() => window.electronAPI.overlay?.setMoving?.(true)}
              data-testid="settings-overlay-move"
              className="text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary"
            >
              Move overlay
            </button>
            <button
              type="button"
              onClick={() => {
                window.electronAPI.overlay?.resetPosition?.();
                toast.success("Overlay moved back to the top of the screen");
              }}
              data-testid="settings-overlay-reset"
              className="text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary"
            >
              Reset position
            </button>
          </div>

          <div className="mt-4 text-xs text-muted-foreground leading-relaxed" data-testid="settings-overlay-hotkeys">
            Works with the sim in front, and from the tray icon too:
            <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <kbd className="font-mono text-foreground">Ctrl+Alt+H</kbd><span>Hide or show the overlay</span>
              <kbd className="font-mono text-foreground">Ctrl+Alt+M</kbd><span>Move it: drag, then press Done (or the keys again)</span>
              <kbd className="font-mono text-foreground">Ctrl+Alt+V</kbd><span>Show or hide the METAR row</span>
            </div>
          </div>
        </section>
      )}

      <button
        onClick={() => savePrefs.mutate()}
        disabled={savePrefs.isPending}
        data-testid="settings-save"
        className="mb-6 bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest px-5 py-2.5 rounded-md hover:brightness-110 flex items-center gap-2 disabled:opacity-60"
      >
        {savePrefs.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Save settings
      </button>

      <section className="card-flat p-6" data-testid="settings-account">
        <div className="overline mb-1">Account</div>
        <h2 className="text-base font-semibold mb-4">Pilot record</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Row label="Pilot ID" value={user?.pid} mono />
          <Row label="Name" value={`${user?.first_name || ""} ${user?.last_name || ""}`} />
          <Row label="Email" value={user?.email} mono />
          <Row label="Hub" value={user?.hub} mono />
          <Row label="Rank" value={card?.rank_title || card?.rank_short || "—"} />
          <Row label="VATSIM" value={user?.vatsim || "—"} mono />
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
